/**
 * Panel — translucent, bordered HUD surface.
 *
 * Glass effect via backdrop-filter, single hairline border in the tone color,
 * subtle inset glow, and an animated entrance from translateY(8px) + opacity 0.
 * Used as the wrapper for every command-rail, telemetry block, and overlay.
 */

import { motion, type HTMLMotionProps } from 'framer-motion';
import { forwardRef, type ReactNode } from 'react';
import { tokens } from './tokens.js';

export type PanelTone = 'neutral' | 'info' | 'warn' | 'critical';

export interface PanelProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  readonly tone?: PanelTone;
  readonly title?: string;
  readonly children?: ReactNode;
  readonly padding?: number | string;
}

const toneToBorder: Record<PanelTone, string> = {
  neutral: tokens.color.border.neutral,
  info: tokens.color.border.info,
  warn: tokens.color.border.warn,
  critical: tokens.color.border.critical,
};

export const Panel = forwardRef<HTMLDivElement, PanelProps>(function Panel(
  { tone = 'neutral', title, children, padding = 16, style, ...rest },
  ref,
) {
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 4 }}
      transition={{ duration: tokens.motion.medium / 1000, ease: [0.2, 0.8, 0.2, 1] }}
      style={{
        position: 'relative',
        padding,
        background: tokens.color.surface.glass,
        border: `1px solid ${toneToBorder[tone]}`,
        borderRadius: 4,
        backdropFilter: 'blur(12px) saturate(140%)',
        WebkitBackdropFilter: 'blur(12px) saturate(140%)',
        boxShadow: `inset 0 0 24px ${tokens.color.surface.innerGlow}, 0 1px 0 ${tokens.color.surface.topHighlight}`,
        color: tokens.color.text.primary,
        fontFamily: tokens.type.family.sans,
        ...style,
      }}
      {...rest}
    >
      {title !== undefined && (
        <div
          style={{
            fontFamily: tokens.type.family.mono,
            fontSize: tokens.type.size.xs,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: tokens.color.text.muted,
            marginBottom: 8,
          }}
        >
          {title}
        </div>
      )}
      {children}
    </motion.div>
  );
});
